/**
 * Bewegungshelfer der Portal-Hinweis-Animationen — dieselben Formeln wie die
 * Claude-Design-Engine (`animations-v3.jsx`: Easing, interpolate, animate,
 * clamp), damit ein portierter Film Bild für Bild dem Entwurf entspricht.
 */

export type Ease = (t: number) => number;

export const Easing = {
  linear: ((t) => t) as Ease,
  easeOutCubic: ((t) => {
    const u = t - 1;
    return u * u * u + 1;
  }) as Ease,
  easeInOutCubic: ((t) =>
    t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1) as Ease,
};

export const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

/** Stückweise lineare Abbildung über Stützstellen, je Abschnitt mit `ease`. */
export function interpolate(input: number[], output: number[], ease: Ease = Easing.linear) {
  return (t: number): number => {
    if (t <= input[0]) return output[0];
    const last = input.length - 1;
    if (t >= input[last]) return output[last];
    for (let i = 0; i < last; i++) {
      if (t >= input[i] && t <= input[i + 1]) {
        const span = input[i + 1] - input[i];
        const local = span === 0 ? 0 : (t - input[i]) / span;
        return output[i] + (output[i + 1] - output[i]) * ease(local);
      }
    }
    return output[last];
  };
}

/** 0 → 1 zwischen `start` und `end`; davor 0, danach 1. */
export function tween(start: number, end: number, t: number, ease: Ease): number {
  if (t <= start) return 0;
  if (t >= end) return 1;
  return ease((t - start) / (end - start));
}

/** Ein Hinweis-Film: baut sein DOM einmal und malt jedes Bild allein aus T. */
export interface HintAnimation {
  /** Bühnengröße in px (der Gastgeber skaliert sie auf die Box). */
  readonly width: number;
  readonly height: number;
  /** Abspieldauer eines Durchlaufs in Sekunden. */
  readonly playbackSeconds: number;
  /** Der Zeitpunkt (Abspielzeit), der bei `prefers-reduced-motion` als Standbild steht. */
  readonly stillAt: number;
  mount(host: HTMLElement): void;
  /** Malt das Bild zur Abspielzeit `t` (0 ≤ t < playbackSeconds). */
  render(t: number): void;
}
